import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import {
    Table, Input, Button, Tag, Space, Dropdown, Menu,
    notification, Modal, Select, Tooltip,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, ReloadOutlined, MoreOutlined,
    CopyOutlined, DeleteOutlined, ExperimentOutlined,
    EyeOutlined, ExportOutlined, EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
    getAccountingPlans, deletePlan, duplicatePlan,
} from '../../../api/payments';
import { FREQUENCY_COLORS, FREQUENCY_OPTIONS } from '../common/constants';
import { PlanEditorModal } from './PlanEditorModal';
import styles from '../common/styles.module.scss';

interface PlanItem {
  _id: string;
  name: string;
  frequency: string;
  createdAt: string;
  updatedAt: string;
}

const PlansTab: React.FC = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<PlanItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [frequencyFilter, setFrequencyFilter] = useState<string | undefined>(undefined);
    const [paginator, setPaginator] = useState({ page: 1, limit: 10 });
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const searchTimer = useRef<ReturnType<typeof setTimeout>>();

    const [editorVisible, setEditorVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState<any>(null);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getAccountingPlans({
                paginator: { page: paginator.page, limit: paginator.limit },
                search: search || undefined,
            });
            setPlans(data.data?.data || data.data || []);
            setTotal(data.data?.total || data.total || 0);
        } catch (err) {
            notification.error({ message: 'Error loading plans' });
        } finally {
            setLoading(false);
        }
    }, [paginator, search]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleDuplicate = async (planId: string) => {
        try {
            await duplicatePlan(planId);
            notification.success({ message: 'Plan duplicado' });
            fetchPlans();
        } catch (err) {
            notification.error({ message: 'Error duplicando plan' });
        }
    };

    const handleDelete = (planId: string, planName: string) => {
        Modal.confirm({
            title: 'Eliminar Plan',
            content: `¿Seguro que quieres eliminar "${planName}"? Esta acción no se puede deshacer.`,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deletePlan(planId);
                    notification.success({ message: 'Plan eliminado' });
                    fetchPlans();
                } catch (err) {
                    notification.error({ message: 'Error eliminando plan' });
                }
            },
        });
    };

    const handleSearch = (value: string) => {
        setSearchInput(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setSearch(value);
            setPaginator((prev) => ({ ...prev, page: 1 }));
        }, 400);
    };

    const handleExport = () => {
        const csvContent = [
            ['Name', 'Frequency', 'Created At'].join(','),
            ...plans.map((p) => [
                `"${p.name}"`,
                p.frequency,
                moment(p.createdAt).format('YYYY-MM-DD'),
            ].join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `plans_${moment().format('YYYY-MM-DD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleViewDetail = (record: PlanItem) => {
        navigate(`/v2/finance-engine/plan/${record._id}`);
    };

    const handleEdit = (record: any) => {
        setEditingPlan(record);
        setEditorVisible(true);
    };

    const handleCreate = () => {
        setEditingPlan({});
        setEditorVisible(true);
    };

    const handleEditorClose = (updatedPlan?: any) => {
        setEditorVisible(false);
        setEditingPlan(null);
        if (updatedPlan) {
            fetchPlans();
        }
    };

    const filteredPlans = frequencyFilter
        ? plans.filter((p) => p.frequency === frequencyFilter)
        : plans;

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: PlanItem) => (
                <Button
                    type="link"
                    style={{ padding: 0, fontWeight: 500 }}
                    onClick={() => handleViewDetail(record)}
                >
                    {name}
                </Button>
            ),
        },
        {
            title: 'Frequency',
            dataIndex: 'frequency',
            key: 'frequency',
            width: 140,
            render: (freq: string) => (
                <Tag color={FREQUENCY_COLORS[freq] || 'default'} style={{ fontSize: 12 }}>
                    {freq}
                </Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            render: (date: string) => moment(date).format('MMM DD, YYYY'),
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: any, record: PlanItem) => (
                <Dropdown
                    overlay={(
                        <Menu>
                            <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
                                Ver detalle
                            </Menu.Item>
                            <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                                Editar
                            </Menu.Item>
                            <Menu.Item key="test" icon={<ExperimentOutlined />} onClick={() => navigate(`/v2/finance-engine/${record._id}`)}>
                                Probar
                            </Menu.Item>
                            <Menu.Item key="duplicate" icon={<CopyOutlined />} onClick={() => handleDuplicate(record._id)}>
                                Duplicar
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record._id, record.name)}>
                                Eliminar
                            </Menu.Item>
                        </Menu>
                    )}
                    trigger={['click']}
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div>
            <div className={styles.searchBar} id="fev2-plans-search">
                <Input
                    placeholder="Search plans..."
                    prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                    style={{ width: 300 }}
                    allowClear
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                />
                <Select
                    placeholder="Frecuencia"
                    style={{ width: 160 }}
                    allowClear
                    options={FREQUENCY_OPTIONS}
                    value={frequencyFilter}
                    onChange={setFrequencyFilter}
                />
                <Space style={{ marginLeft: 'auto' }}>
                    <Tooltip title="Export CSV">
                        <Button icon={<ExportOutlined />} onClick={handleExport}>
                            Export
                        </Button>
                    </Tooltip>
                    <Tooltip title="Reload">
                        <Button icon={<ReloadOutlined />} onClick={fetchPlans} />
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Crear Plan
                    </Button>
                </Space>
            </div>

            <div className={styles.tableWrapper}>
                <Table
                    dataSource={filteredPlans}
                    columns={columns}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: paginator.page,
                        pageSize: paginator.limit,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        onChange: (page, pageSize) => setPaginator({ page, limit: pageSize || 10 }),
                    }}
                />
            </div>

            <PlanEditorModal
                visible={editorVisible}
                plan={editingPlan}
                onClose={handleEditorClose}
            />
        </div>
    );
};

export { PlansTab };
